import React from 'react';

import {
    Modal,
    Button,
    Box,
    SpaceBetween,
    Alert,
} from '@amzn/awsui-components-react-v3';
import { DeliverySession } from '../../../interfaces/activity';
import { formatUnixTimeInTimezone } from '../../../../common/utils/date-time.utils';

interface ValidateInstructorBusyModalProps {
    visible: boolean;
    onCancel: any;
    onConfirm: any;
    instructorConflict: any;
    deliverySessions: Array<DeliverySession>;
    activityTimezone: string;
}

/**
 * Returns the formatted dates of delivery sessions that overlap with any of
 * the given conflict time ranges. Deduplicates by formatted date string.
 */
const getOverlappedSessionDates = (
    conflictRanges: Array<{ start_timestamp: number; end_timestamp: number }>,
    deliverySessions: Array<DeliverySession>,
    timezone: string,
): string[] =>
    Array.from(
        new Set(
            deliverySessions
                .filter(
                    (session) =>
                        session.start_timestamp != null &&
                        session.end_timestamp != null &&
                        conflictRanges.some(
                            (range) =>
                                range.start_timestamp <
                                    (session.end_timestamp as number) &&
                                session.start_timestamp! < range.end_timestamp,
                        ),
                )
                .map((session) =>
                    formatUnixTimeInTimezone({
                        timestamp: session.start_timestamp as number,
                        timezone,
                        format: 'ddd, MMM D',
                    }),
                ),
        ),
    );

const ValidateInstructorBusyModal = ({
    onCancel,
    onConfirm,
    instructorConflict,
    visible,
    deliverySessions,
    activityTimezone,
}: ValidateInstructorBusyModalProps) => {
    const instructor = instructorConflict?.instructor;

    const blockedDates = getOverlappedSessionDates(
        (instructor?.blocked_times || []).flatMap(
            (bt: any) => bt.blocked_time_timestamps || [],
        ),
        deliverySessions,
        activityTimezone,
    );

    const doNotShuffleDates = getOverlappedSessionDates(
        (instructor?.do_not_shuffle_activities || []).flatMap((activity: any) =>
            (activity.delivery_sessions || [])
                .filter(
                    (s: any) =>
                        s.start_timestamp != null && s.end_timestamp != null,
                )
                .map((s: any) => ({
                    start_timestamp: s.start_timestamp,
                    end_timestamp: s.end_timestamp,
                })),
        ),
        deliverySessions,
        activityTimezone,
    );

    return (
        <Modal
            onDismiss={() => onCancel()}
            visible={visible}
            closeAriaLabel="Close modal"
            size="medium"
            data-testid={`NoShuffleModal`}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            variant="link"
                            data-testid={`NoShuffleModalCancel`}
                            onClick={() => {
                                onCancel();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            data-testid={`NoShuffleModalConfirm`}
                            onClick={() => {
                                onConfirm();
                            }}
                        >
                            Continue with assignment
                        </Button>
                    </SpaceBetween>
                </Box>
            }
            header={`Instructor has scheduling conflicts`}
        >
            <SpaceBetween size="s">
                <Alert
                    onDismiss={() => {}}
                    visible={true}
                    dismissAriaLabel="Close alert"
                    type="warning"
                >
                    {`${instructor?.full_name}`} is unavailable for some
                    sessions on this activity. You will not be able to assign
                    this instructor to any of these conflicting sessions.
                </Alert>
                {blockedDates.length > 0 && (
                    <div
                        className="reassign-adjacent-activity"
                        data-testid="blocked-time-conflicts-section"
                    >
                        <Box variant="h4">Blocked time conflicts</Box>
                        {blockedDates.map((date: string, index: number) => (
                            <Box key={`blocked-date-${index}`}>{date}</Box>
                        ))}
                    </div>
                )}
                {doNotShuffleDates.length > 0 && (
                    <div
                        className="reassign-adjacent-activity"
                        data-testid="unshuffleable-sessions-section"
                    >
                        <Box variant="h4">Sessions that can't be shuffled</Box>
                        {doNotShuffleDates.map(
                            (date: string, index: number) => (
                                <Box key={`dns-date-${index}`}>{date}</Box>
                            ),
                        )}
                    </div>
                )}
            </SpaceBetween>
        </Modal>
    );
};

export default ValidateInstructorBusyModal;
